<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />

          <Button
            v-if="getUserPermission('CREATE_FIXED_DEPOSIT_ACCOUNT_NIB')"
            :label="$t('Add')"
            size="md"
            @click="goToAddFDAccount"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="fdRef"
      :busy="isBusy"
      :items="fdAccountList"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(principalAmount)="data">
        {{ data.value }}
      </template>
      <template #cell(ledgerBalance)="data">
        {{ data.value }}
      </template>
      <template #cell(commencementDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>
      <template #cell(maturityDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>
      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(accountStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <template #cell(disbursedFD)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="getUserPermission('MODIFY_FIXED_DEPOSIT_ACCOUNT_NIB')"
              @click="goToEditFDAccount(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">
                {{ $t('Modify') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="!data.item.hasDisbursed"
              @click="onDisburse(data.item, $event.target)"
            >
              <feather-icon icon="CreditCardIcon" />
              <span class="align-middle ml-50">
                {{ $t('Book FD') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission('EXPORT_FIXED_DEPOSIT_CERTIFICATE')"
              @click="onFDCertificate(data.item, $event.target)"
            >
              <feather-icon icon="AwardIcon" />
              <span class="align-middle ml-50">
                {{ $t('FD Certificate') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="goToEditFDAccount(data.item, $event.target, 'view')"
            >
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchFixedDepositAccounts"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!--Customer ID -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.customerID"
            :label="$t('Customer ID')"
            :placeholder="$t('Enter Customer ID')"
            type="text"
          />
        </b-col>
        <!-- Customer ID -->

        <!--FD Account Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.accountName"
            :label="$t('FD Account Name')"
            :placeholder="$t('Enter FD account name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- FD Account Name -->

        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="number"
          />
        </b-col>
        <!-- Account Number -->

        <!-- Product -->
        <b-col md="6">
          <SelectField
            id="product"
            v-model="filter.productCode"
            :label="$t('Product')"
            :reduce="(option) => option.value"
            :options="productOptions"
            item="label"
            :placeholder="$t('Select product')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Product -->
        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :label="$t('Branch')"
            :reduce="(option) => option.value"
            :options="branchOptions"
            item="label"
            :placeholder="$t('Select branch')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Branch -->

        <b-col
          md="6"
        >
          <SelectField
            id="interest-payment-frequency"
            v-model="filter.interestPayoutFrequency"
            :label="$t('Profit Payment Frequency')"
            :reduce="option => option.value"
            :options="translatedInterestPayoutFrequencyOptions"
            :placeholder="$t('Select')"
            item="label"
            :readonly="isReadonly"
          />
        </b-col>

        <!-- Account Officer  -->
        <b-col md="6">
          <SelectField
            id="account-officer"
            v-model="filter.accountOfficerCode"
            :label="$t('Account Officer')"
            :reduce="option => option.value"
            :options="accountOfficerOptions"
            item="label"
            :placeholder="$t('Select account officer')"
          />
        </b-col>
        <!--Account Officer  -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.accountStatus"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="translatedFdAccountStatusOptions"
            item="label"
            :reduce="(option) => option.value"
          />
        </b-col>
        <!-- Status -->
        <!-- Book FD Status -->
        <b-col md="6">
          <SelectField
            id="disburse-status"
            v-model="filter.hasDisbursed"
            :label="$t('Book FD Status')"
            :placeholder="$t('Select')"
            :options="translatedFdAccountBookStatusOptions"
            item="label"
            :reduce="(option) => option.value"
          />
        </b-col>
        <!-- Book FD Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import { modalMixins } from '@core/mixins/modal'
import { permissionMixin } from '@core/mixins/permission'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { maxValue, positive } from '@validations'
import {
  BBadge,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BModal,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { mapActions, mapGetters } from 'vuex'
import * as FileSaver from 'file-saver'
import addSpaceToString from '@/helpers/addSpaceToString'
import convertToWords from '@/helpers/amountToWords'
import { toBalance } from '@/helpers/formatAmount'
import {
  toDateWithMinute,
  toDateWithMonth,
  toDateWithYear,
  toMonthWithYear,
  toYearWithDate,
  tocustomDate,
  toDayMonthYear,
} from '@/helpers/formatDate'
import getStatusColor from '@/helpers/getStatusColor'
import toNumber from '@/helpers/toNumber'
import { transformBoolean } from '@/helpers/transformers'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import TablePagination from '@/views/components/table/TablePagination.vue'
import { accountPermissions } from '../../../../../helpers/permissions'
import Button from '../../../../components/button/Button.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BTable,
    BDropdown,
    BDropdownItem,
    BModal,
    BSpinner,
    SelectField,
    InputField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      showSpinner: false,
      isReadonly: false,
      isLoading: false,
      isDisabled: false,
      maxValue,
      toBalance,
      toNumber,
      convertToWords,
      addSpaceToString,
      positive,
      fixedDepositSchedulePagination: {
        currentPage: 1,
        totalEntries: 0,
        perPage: 10,
        pageSize: 0,
      },
      getStatusColor,
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'name', sortable: true, label: 'FD Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        {
          key: 'liquidationAccount',
          sortable: true,
          label: 'Liquidation Account',
        },
        { key: 'productName', sortable: true, label: 'Product' },
        { key: 'branchName', sortable: true, label: 'Branch' },
        { key: 'accountOfficerName', sortable: true, label: 'Account Officer Name' },
        { key: 'principalAmount', sortable: true, label: 'Principal Amount' },
        {
          key: 'applicableInterestRate',
          sortable: true,
          label: 'Profit Rate',
        },

        { key: 'tenure', sortable: true, label: 'Duration' },
        { key: 'commencementDate', sortable: true, label: 'Commencement Date' },
        { key: 'maturityDate', sortable: true, label: 'Maturity Date' },
        { key: 'interestPayoutFrequency', sortable: true, label: 'Profit Payment Frequency' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'accountStatus', sortable: true, label: 'Account Status' },
        { key: 'disbursedFD', sortable: true, label: 'Book FD' },
        { key: 'actions', label: 'Actions' },
      ],

      toDateWithYear,
      toMonthWithYear,
      toDateWithMonth,
      toYearWithDate,
      toDateWithMinute,
      tocustomDate,
      toDayMonthYear,
      buttonLabel: 'Save',
      isFlatAmountRequired: '',
      isPercentageRequired: '',
      isFlatAmount: false,
      isPercentage: false,
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        ariaDateFormat: 'd/m/Y',
      },
      filter: {
        accountName: '',
        accountNumber: '',
        productCode: '',
        branchCode: '',
        accountStatus: '',
        interestPayoutFrequency: '',
        hasDisbursed: '',
        sortColumn: '',
        direction: '',
        accountOfficerCode: '',
      },
      fdAccountStatusOptions: [
        { value: 'Active', label: 'Active' },
        { value: 'Closed', label: 'Closed' },
      ],
      fdAccountBookStatusOptions: [
        { value: 'True', label: 'Yes' },
        { value: 'False', label: 'No' },
      ],
      interestPayoutFrequencyOptions: [
        { label: 'Monthly', value: 'Monthly' },
        { label: 'Quarterly', value: 'Quarterly' },
        { label: 'Half Yearly', value: 'HalfYearly' },
      ],
      fdAccountList: [],
      productOptions: [],
      branchOptions: [],
      certificateData: {},
      customerAddress: '',
      accountOfficerOptions: [],
      corporateInfo: {},
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      corporateDetails: 'operations/getCorporateDetails',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedInterestPayoutFrequencyOptions() {
      return this.interestPayoutFrequencyOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedFdAccountBookStatusOptions() {
      return this.fdAccountBookStatusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedFdAccountStatusOptions() {
      return this.fdAccountStatusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchFixedDepositAccounts()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchFixedDepositAccounts()
    },
  },
  mounted() {
    this.fetchFixedDepositAccounts()
    this.fetchCASAProducts()
    this.fetchBranches()
    this.fetchAccountOfficers()
    this.fetchInstitutionFileUpload()
  },

  methods: {
    ...mapActions({
      getFdAccounts: 'account/getFdAccountList',
      getCASAProduct: 'account/getProducts',
      disburseFixedDeposit: 'account/disburseFixedDeposit',
      getBranch: 'operations/getBranchList',
      downloadFixedDepositCertificate: 'account/downloadFixedDepositCertificate',
      getAccountOfficers: 'account/getAccountOfficerList',
      getInstitutionFileUpload: 'operations/getInstitutionFileUploadList',
    }),
    async fetchInstitutionFileUpload() {
      try {
        const { status, data } = await this.getInstitutionFileUpload({ permission: accountPermissions.VIEW_FIXED_DEPOSIT_ACCOUNT_NIB })

        if (status === 200 && data) {
          this.corporateInfo.stamp = data.stampUrl || ''
          this.corporateInfo.signature = data.eSignatureUrl || ''
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    goToAddFDAccount() {
      this.$router
        .push({
          name: 'fixed-deposit-nib',
          params: {
            type: 'add',
          },
        })
        .catch(error => {
          this.alertError({ text: error })
        })
    },
    goToEditFDAccount(item, activity, val) {
      this.$router
        .push({
          name: 'fixed-deposit-nib',
          params: {
            type: val,
            id: item.id,
          },
        })
        .catch(error => {
          this.alertError({ text: error })
        })
    },
    async fetchAccountOfficers() {
      try {
        const { data, status } = await this.getAccountOfficers({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_FIXED_DEPOSIT_ACCOUNT_NIB,
        })

        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    onDisburse(items) {
      const { name } = items
      const disburseFixedDepositAccount = async () => {
        try {
          this.showSpinner = true
          const response = await this.disburseFixedDeposit({
            accountNumber: items.accountNumber,
            initiatedBy: this.userDetails.email,
          })
          const { data, status } = response
          if (status === 200 && data.isSuccessful) {
            this.fetchFixedDepositAccounts()
            this.alertSuccess({
              text: 'Your request to Book FD Account was successful',
            })
            this.showSpinner = false
          }
          if (status === 200 && !data.isSuccessful) {
            this.alertError({
              text: `Your request to Book FD Account failed. Reason: ${data.message}`,
            })
          }
        } catch (error) {
          error.response.data.errors.forEach(responseError => {
            this.alertError({
              text: `Your request to Book FD Account failed. Reason: ${responseError.errorMessage}`,
            })
          })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(disburseFixedDepositAccount, {
        title: '',
        text: `Are you sure you want to Book ${name}'s FD Account?`,
      })
    },
    async onFDCertificate(items) {
      try {
        this.showSpinner = true
        this.isBusy = true
        const response = await this.downloadFixedDepositCertificate({
          accountNumber: items.accountNumber,
          permission: accountPermissions.EXPORT_FIXED_DEPOSIT_CERTIFICATE,
        })
        if (response.status === 200) {
          const fileName = this.$t('Fixed Deposit Certificate')
          const blob = new Blob([response.data], { type: 'application/pdf' })
          FileSaver.saveAs(blob, `${fileName}.pdf`)
          this.alertSuccess({ text: this.$t('Your request to download Fixed Deposit Certificate was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
        this.isBusy = false
      }
    },
    async fetchFixedDepositAccounts() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getFdAccounts({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.fdAccountList = data.data.map(item => ({
            ...item,
            disbursedFD: transformBoolean(item.hasDisbursed),
            principalAmount: toBalance(item.principalAmount),
          }))
          this.totalCount = data.recordCount
          this.isBusy = false
        } else {
          this.fdAccountList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
        this.isBusy = false
      } finally {
        this.isBusy = false
      }
    },
    async fetchCASAProducts() {
      try {
        const response = await this.getCASAProduct({
          search: { type: 'FixedDeposit' },
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_FIXED_DEPOSIT_ACCOUNT_NIB,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.map(item => ({
            label: item.name,
            value: item.id,
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchBranches() {
      try {
        const response = await this.getBranch({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_FIXED_DEPOSIT_ACCOUNT_NIB,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.branchOptions = data.data
            .filter(dataItem => dataItem.status === 'Active')
            .map(item => ({ label: item.name, value: item.id }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchFixedDepositAccounts()
    },
  },
}
</script>
<style lang="scss">
#toPrinter {
  display: none;
}
</style>
<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
    @media print {
  html, body {
     height: 99%;
     page-break-after: avoid !important;
     page-break-before: avoid !important;
     visibility:hidden;
  }
  #toPrinter {
    display: block;
  }
}
.print-color {
  color: #000000;
}
</style>
